import { useEffect, useState, useMemo } from "react";
import BarChart from "./BarChart";
import {
  CHART_BG_COLORS,
  CHART_BORDER_COLORS,
  check_date_within_range,
} from "../../utils/reports";
import {
  IdentifiedObjectItem,
  IdentifiedSpeciesCount,
} from "../../models/Media";
import { CameraItem } from "../../models/Camera";

interface SpeciesByHourProps {
  cameras: CameraItem[];
  selectedCameras: CameraItem[];
  allIdentifiedObjects: IdentifiedObjectItem[];
  selectedIdentifiedObjects: IdentifiedObjectItem[];
  reportData: IdentifiedSpeciesCount[];
  startDate: string;
  endDate: string;
}

const hourNames = Array.from({ length: 24 }, (_, i) => `${i}:00`);

// Preprocess reportData into a Map for fast lookups
export const useProcessedReportData = (
  reportData: IdentifiedSpeciesCount[],
  startDate: string,
  endDate: string
) => {
  return useMemo(() => {
    const map = new Map();
    reportData.forEach((data) => {
      if (!check_date_within_range(startDate, endDate, data.date)) return;
      const key = `${data.identified_object_name}-${data.camera}`;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push({ date: data.date, video_count: 1 });
    });
    return map;
  }, [reportData, startDate, endDate]);
};

// Optimized function to get count per hour
export const getCount = (
  objectName: string,
  hourIndex: number,
  cameraId: string,
  dataMap: Map<string, any[]>
) => {
  const key = `${objectName}-${cameraId}`;
  const matchingData = dataMap.get(key) || [];
  return matchingData.reduce((total, data) => {
    const dataHour = new Date(data.date).getUTCHours();
    return dataHour === hourIndex ? total + 1 : total;
  }, 0);
};

// Optimized function to get total count across all cameras
export const getTotalCount = (
  objectName: string,
  hourIndex: number,
  selectedCameras: CameraItem[],
  dataMap: Map<string, any[]>
) => {
  return selectedCameras.reduce(
    (total, camera) =>
      total + getCount(objectName, hourIndex, camera.camera_id, dataMap),
    0
  );
};

const LocationSpeciesByHour = ({
  cameras,
  selectedCameras,
  allIdentifiedObjects,
  selectedIdentifiedObjects,
  reportData,
  startDate,
  endDate,
}: SpeciesByHourProps) => {
  const [mapData, setMapData] = useState<any>(null);
  const [csvData, setCsvData] = useState<any>();

  const dataMap = useProcessedReportData(reportData, startDate, endDate);
  const ChartName = "Species Observed by Hour of the Day";

  // **Generate CSV Data**
  useEffect(() => {
    const csv = selectedCameras.flatMap((camera) =>
      selectedIdentifiedObjects.flatMap((object) =>
        hourNames.map((_, hourIndex) => ({
          camera: camera.camera_id,
          Object: object.name,
          hour: hourIndex,
          Count: getCount(object.name, hourIndex, camera.camera_id, dataMap),
        }))
      )
    );
    setCsvData(csv);
  }, [selectedCameras, selectedIdentifiedObjects, dataMap]);

  // Generate Chart Data
  useEffect(() => {
    if (!selectedCameras.length || !selectedIdentifiedObjects.length) return;

    const datasets = selectedIdentifiedObjects.map((object, index) => ({
      label: object.name,
      data: hourNames.map((_, hourIndex) =>
        getTotalCount(object.name, hourIndex, selectedCameras, dataMap)
      ),
      backgroundColor:
        CHART_BG_COLORS[
          Object.keys(CHART_BG_COLORS)[
            index % Object.keys(CHART_BG_COLORS).length
          ]
        ],
      borderColor:
        CHART_BORDER_COLORS[
          Object.keys(CHART_BORDER_COLORS)[
            index % Object.keys(CHART_BORDER_COLORS).length
          ]
        ],
      borderWidth: 1,
    }));

    setMapData({ labels: hourNames, datasets });
  }, [selectedCameras, selectedIdentifiedObjects, dataMap]);

  // Optimize Y-Axis Scaling
  const getYAxisConfig = (datasets: any[]) => {
    const allValues = datasets?.flatMap((dataset) => dataset.data) || [];
    const maxValue = Math.max(10, ...allValues);
    const stepSize = maxValue < 10 ? 1 : maxValue < 100 ? 10 : 50;
    return {
      suggestedMax: Math.ceil(maxValue / stepSize) * stepSize,
      stepSize,
    };
  };

  const { suggestedMax, stepSize } = mapData
    ? getYAxisConfig(mapData.datasets)
    : { suggestedMax: 10, stepSize: 1 };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" as const },
      title: { display: true, text: "Animals Observed by Time-of-Day" },
    },
    layout: { padding: { left: 0, right: 10, top: 0, bottom: 0 } },
    scales: {
      y: { min: 0, suggestedMax, ticks: { stepSize } },
    },
  };

  return (
    <BarChart
      mapData={mapData}
      options={options}
      csvData={csvData}
      name={ChartName}
    />
  );
};

export default LocationSpeciesByHour;
